import { ArrowUpRight, Dumbbell, Info } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { CENTR_GEAR, DPS_MEDIA } from "../lib/content";
import { trackEvent } from "../lib/analytics";

const GearCard = ({ item, testid }) => (
  <a
    href={item.href}
    target="_blank"
    rel="sponsored noopener noreferrer"
    className="group bg-[#0c0c0c] p-6 h-full flex flex-col hover:bg-[#101010] transition-colors"
    data-testid={testid}
    onClick={() => trackEvent("affiliate_click", { partner: "centr", item: item.name })}
  >
    <div className="flex items-start justify-between gap-3 mb-2">
      <h3 className="font-head font-bold uppercase text-lg leading-tight group-hover:text-volt transition-colors">{item.name}</h3>
      <ArrowUpRight size={16} className="text-zinc-600 group-hover:text-volt shrink-0 transition-colors" />
    </div>
    {item.maps && <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-volt mb-2">{item.maps}</p>}
    {item.blurb && <p className="text-zinc-400 text-sm leading-relaxed">{item.blurb}</p>}
  </a>
);

export default function Gear() {
  return (
    <div data-testid="gear-page" className="pt-16">
      <Seo
        title="Race-Standard HYROX Gear — Centr Picks | HyroxNinja"
        description="Train the 8 stations at home with race-standard gear: official HYROX bundles, sleds, sandbags, kettlebells, wall balls and treadmills — hand-picked Centr equipment used in Hyrox Ninja training."
      />
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.bg.stations} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
          <p className="eyebrow mb-4 flex items-center gap-2"><Dumbbell size={14} /> Train It at Home</p>
          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
            <MaskedLine delay={0.1}>Race-Standard</MaskedLine>
            <MaskedLine delay={0.25} className="text-volt">Gear</MaskedLine>
          </h1>
          <p className="text-zinc-400 max-w-2xl leading-relaxed mb-5">
            The same categories of equipment we train on, from Centr — official HYROX bundles plus
            the individual pieces behind every station. Build your home setup to the standard the
            race holds you to.
          </p>
          <p className="flex items-start gap-2 text-zinc-500 text-xs leading-relaxed max-w-2xl" data-testid="affiliate-disclosure">
            <Info size={14} className="shrink-0 mt-0.5 text-volt" />
            <span>
              Affiliate disclosure: these are affiliate links — if you buy through them, Hyrox Ninja
              earns a commission at no extra cost to you. It goes straight back into the program.
            </span>
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="gear-bundles-section">
        <Reveal>
          <p className="eyebrow mb-4">Official HYROX Bundles</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {CENTR_GEAR.bundles.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.04} className="h-full">
              <GearCard item={b} testid={`gear-bundle-${i}`} />
            </Reveal>
          ))}
          <Reveal delay={0.2} className="h-full">
            <a
              href={CENTR_GEAR.shopAll}
              target="_blank"
              rel="sponsored noopener noreferrer"
              className="group bg-volt text-black p-6 h-full flex flex-col justify-between hover:bg-[#e6d600] transition-colors"
              data-testid="gear-shop-all"
              onClick={() => trackEvent("affiliate_click", { partner: "centr", item: "shop_all" })}
            >
              <h3 className="font-head font-black uppercase text-2xl leading-tight">Shop the full Centr range</h3>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 mt-4">
                centr.com <ArrowUpRight size={14} />
              </p>
            </a>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="gear-stations-section">
        <Reveal>
          <p className="eyebrow mb-4">Station-by-Station Gear</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {CENTR_GEAR.stations.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.03} className="h-full">
              <GearCard item={s} testid={`gear-station-${i}`} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="gear-machines-section">
        <Reveal>
          <p className="eyebrow mb-4">Machines & Home Setups</p>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {CENTR_GEAR.machines.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.04} className="h-full">
              <GearCard item={m} testid={`gear-machine-${i}`} />
            </Reveal>
          ))}
        </div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-6">
          Affiliate links · Hyrox Ninja may earn a commission · prices set by Centr
        </p>
      </section>
    </div>
  );
}
